import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { formatINR } from "@/components/ChefCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Calendar as CalIcon, MapPin, ChefHat, ArrowRight, FileSignature, FileCheck2, Receipt } from "lucide-react";
import { Link } from "react-router-dom";
import { payForBooking } from "@/lib/payments";
import AgreementDialog from "@/components/AgreementDialog";

const STATUS_STYLE = {
  pending: "text-[#F5B041] border-[#F5B041]/40",
  assigned: "text-blue-300 border-blue-300/40",
  confirmed: "text-green-300 border-green-300/40",
  paid: "text-green-400 border-green-400/40",
  in_progress: "text-[#F5B041] border-[#F5B041]/40",
  completed: "text-neutral-300 border-white/20",
  cancelled: "text-red-400 border-red-400/40",
};

export default function MyBookings() {
  const { user, setAuthOpen } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agreementFor, setAgreementFor] = useState(null);

  const openInvoice = async (id) => {
    const w = window.open("", "_blank");
    try {
      const r = await api.get(`/bookings/${id}/invoice`);
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.invoice.url}`;
      if (w) w.location = url; else window.open(url, "_blank");
    } catch (e) {
      if (w) w.close();
      toast.error(e?.response?.data?.detail || "Invoice not available yet");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/bookings");
      setBookings(r.data.bookings);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user) return;
    load();
  }, [user]);

  const cancel = async (id) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status: "cancelled" });
      toast.success("Booking cancelled");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const payFor = async (id) => {
    try {
      await payForBooking(id);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Payment failed"); }
  };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif-display text-4xl">Please sign in.</h1>
        <p className="text-neutral-400 mt-3">Your bookings live here.</p>
        <Button onClick={() => setAuthOpen(true)} data-testid="signin-cta"
          className="mt-6 rounded-none bg-[#C04000] hover:bg-[#A03500] h-11 small-caps text-[11px]">
          Sign in
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="small-caps text-neutral-500 mb-3">Your table</div>
      <h1 className="font-serif-display text-5xl md:text-6xl leading-none">Bookings.</h1>
      <p className="text-neutral-400 mt-4">All your private chef postings and mini-catering evenings.</p>

      <div className="dotted-sep my-10" />

      {loading ? (
        <div className="text-neutral-500">Loading…</div>
      ) : bookings.length === 0 ? (
        <div data-testid="bookings-list" className="text-center text-neutral-500 py-24">
          <div className="font-serif-menu text-2xl">Nothing yet.</div>
          <p className="mt-2">
            <Link to="/chefs" className="underline underline-offset-4 text-neutral-300">Browse chefs</Link>
            {" · "}
            <Link to="/menus" className="underline underline-offset-4 text-neutral-300">Explore menus</Link>
          </p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="bookings-list">
          {bookings.map((b) => (
            <div key={b.id} className="card-lux p-6 md:p-7">
              <div className="grid md:grid-cols-6 gap-6 items-center">
                <div className="md:col-span-3">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`chip capitalize ${STATUS_STYLE[b.status] || ""}`}>{b.status.replace("_", " ")}</span>
                    <span className="chip capitalize">
                      {b.service_type === "private_chef" ? <ChefHat size={10} className="mr-1" /> : null}
                      {b.service_type.replace("_", " ")}
                    </span>
                  </div>
                  <div className="font-serif-display text-2xl">
                    {b.chef_name || b.menu_title || "Custom request"}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-neutral-400 mt-2">
                    <span className="flex items-center gap-1"><MapPin size={12} /> {b.city}</span>
                    <span className="flex items-center gap-1"><CalIcon size={12} /> {b.start_date || b.event_date || "TBD"}</span>
                    {b.guests && <span>· {b.guests} guests</span>}
                    {b.duration_months && b.service_type === "private_chef" && <span>· {b.duration_months} mo</span>}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="small-caps text-[10px] text-neutral-500">Chef Hire payable</div>
                  <div className="font-serif-menu text-2xl">₹{formatINR((b.commission_amount || 0) + (b.gst_amount || 0))}</div>
                  <div className="text-xs text-neutral-500 mt-1">Total: ₹{formatINR(b.total_amount)}</div>
                </div>
                <div className="md:col-span-1 flex md:flex-col items-end gap-2">
                  {b.payment_status !== "paid" && b.status !== "cancelled" && (
                    (b.total_amount || 0) > 0 ? (
                      <Button size="sm" onClick={() => payFor(b.id)} data-testid={`pay-${b.id}`}
                        className="rounded-none bg-[#C04000] hover:bg-[#A03500] small-caps text-[10px] h-9">
                        Pay <ArrowRight size={12} className="ml-1" />
                      </Button>
                    ) : (
                      <span data-testid={`quote-pending-${b.id}`} className="small-caps text-[10px] text-[#F5B041] border border-[#F5B041]/30 px-3 h-9 inline-flex items-center">
                        Quote pending
                      </span>
                    )
                  )}
                  {!["cancelled","completed"].includes(b.status) && (
                    <button onClick={() => cancel(b.id)} data-testid={`cancel-${b.id}`}
                      className="small-caps text-[10px] text-neutral-500 hover:text-red-400">
                      Cancel
                    </button>
                  )}
                  {b.service_type === "private_chef" && b.status !== "cancelled" && (
                    <button onClick={() => setAgreementFor(b.id)} data-testid={`agreement-${b.id}`}
                      className={`small-caps text-[10px] inline-flex items-center gap-1 ${b.agreement_signed ? "text-[#F5B041]" : "text-neutral-300 hover:text-[#F5B041]"}`}>
                      {b.agreement_signed ? <FileCheck2 size={12} /> : <FileSignature size={12} />}
                      {b.agreement_signed ? "Agreement" : "Sign agreement"}
                    </button>
                  )}
                  {b.payment_status === "paid" && (
                    <button onClick={() => openInvoice(b.id)} data-testid={`invoice-${b.id}`}
                      className="small-caps text-[10px] text-neutral-300 hover:text-[#F5B041] inline-flex items-center gap-1">
                      <Receipt size={12} /> Invoice
                    </button>
                  )}
                </div>
              </div>
              {b.notes && <p className="text-sm text-neutral-500 mt-4 border-t border-white/5 pt-4">Notes: {b.notes}</p>}
              {b.custom_request && <p className="text-sm text-neutral-500 mt-2">Brief: {b.custom_request}</p>}
              {(b.dietary_preferences?.length > 0 || b.allergies?.length > 0 || b.kitchen_equipment?.length > 0 || b.chef_notes) && (
                <div className="mt-4 border-t border-white/5 pt-4 space-y-2 text-xs">
                  {b.dietary_preferences?.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="small-caps text-[9px] text-neutral-500">Diet</span>
                      {b.dietary_preferences.map(d => <span key={d} className="chip text-[#F5B041] text-[9px]">{d}</span>)}
                    </div>
                  )}
                  {b.allergies?.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="small-caps text-[9px] text-neutral-500">Allergies</span>
                      {b.allergies.map(a => <span key={a} className="chip text-[#C04000] border-[#C04000]/40 text-[9px]">{a}</span>)}
                    </div>
                  )}
                  {b.kitchen_equipment?.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="small-caps text-[9px] text-neutral-500">Kitchen</span>
                      <span className="text-neutral-400">{b.kitchen_equipment.join(", ")}</span>
                    </div>
                  )}
                  {b.chef_notes && <p className="text-neutral-500"><span className="small-caps text-[9px]">Chef notes ·</span> {b.chef_notes}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <AgreementDialog
        bookingId={agreementFor}
        open={!!agreementFor}
        onOpenChange={(o) => { if (!o) setAgreementFor(null); }}
        onSigned={load}
      />
    </div>
  );
}
